import Decimal from 'decimal.js'
import { isEmpty, isEqual, isNil, keys, values } from 'lodash-es'
import { Dictionary } from 'utilities/typeUtility/typeUtility'
import {
    ModifierOptionContract,
    OrderContract,
    OrderLineKdsStatus,
    OrderLineLookupContract,
    OrderLineStatus,
    OrderPaymentStatus,
    OrderLookupContract,
    OrderStatus,
    DiscountType,
    OrderDiscountModel,
    OrderDiscountType,
    OrderLineContract,
    OrderLineModifierProcessContract,
    PaymentIntegrationActivityContract,
    PaymentIntegrationEventType,
    ItemContract,
    POSPriceListContract,
    TableServiceOrderLineContract,
    VoidCompReasonModel,
    VoidCompReasonType,
} from 'models'
import {
    DiscountContractWithCustom,
    EntityModifierViewModel,
    ItemMap,
    ItemViewModel,
    IMenuItemDetail,
    MenuViewModel,
    OrderItemViewModel,
    PriceListData,
    UsedItem,
    UsedItems,
    UsedModifier,
    UsedModifiers,
    usedModWithQuantity,
} from 'store/models/sellModels'
import {
    getItemPriceWithAppliedPriceList,
    getModifierOptionPrice,
} from 'utilities/price/getPrice'
import {
    isFreeItem,
    isPromotionItem,
} from '../DiscountBogoDialog/hook/buyXgetYProcess'
import { isOpenItemId, OPEN_ITEM_ID } from 'constant/magicNumber'
import Guid from 'utilities/Guid'
import { validateAvailability as validateTimeAvailability } from 'utilities/availability/availabilityHelper'
import { MENUSECTION_ALL_ITEMS } from 'store/utils/normalizeDependencyData'
import {
    PAYMENT_INTEGRATION_RETURN_TYPES,
    PAYMENT_INTEGRATION_COMPLETE,
} from 'utilities/Payments/PaymentIntegrationStatus'
import { LineResultModel } from 'views/Sale/utils/orderCalculation/models/IOrderCalculationModel'
import { round } from 'views/Sale/utils/orderCalculation/common'
export const NEW_ITEM_INFO: Omit<
    OrderItemViewModel,
    | 'item'
    | 'id'
    | 'amount'
    | 'lineTotal'
    | 'createdOn'
    | 'menuSectionId'
    | 'voidCompQuantity'
> = {
    note: '',
    usedVariants: {},
    usedModifiers: {},
    usedItems: {},
    isSaved: false,
    kdsStatus: OrderLineKdsStatus.Unknown,
    status: OrderLineStatus.New,
    subtotal: new Decimal(0),
    orderID: undefined,
    assignTo: undefined,
}
export function makeCustomDiscountId(itemId: string, index: number) {
    return `custom_discount_${itemId}_${index}`
}

export function isSameProduct(itemPath: string, currentPath: string) {
    return itemPath.includes(currentPath.replace(/(_\d+)/, ''))
}

export function getVariationItem(
    item: ItemViewModel,
    selectedVariantIds: string[],
) {
    if (item.variantGroups.length === selectedVariantIds.length) {
        const usedVariantsList = [...selectedVariantIds]
        usedVariantsList.sort()

        return (
            item.variations.find((i) => {
                if (!i.combinationIds) return false

                const combinationIds = [...i.combinationIds]
                combinationIds.sort()
                return isEqual(combinationIds, usedVariantsList)
            }) ?? item
        )
    }
    return item
}
export function changeBaseItemIDToVariantID(item: OrderItemViewModel) {
    if (Object.keys(item.usedVariants).length > 0) {
        const vItem = getVariationItem(item.item, values(item.usedVariants))
        if (vItem) {
            return {
                ...item,
                item: {
                    ...vItem,
                    // for order panel display and create modify dialog
                    name: item.item.name,
                    displayName: item.item.displayName,
                    variations: item.item.variations,
                    variantGroups: item.item.variantGroups,
                },
            }
        }
    }
    return item
}
export function getUsedItemsInComboChoice(
    usedItems: UsedItems,
    comboChoiceID: string,
) {
    return Object.keys(usedItems)
        .filter((pathToComboItem) => pathToComboItem.includes(comboChoiceID))
        .map((pathToComboItem) => usedItems[pathToComboItem])
}

export function countUsedItemInComboChoice(
    usedItems: UsedItems,
    comboChoiceID: string,
) {
    return getUsedItemsInComboChoice(usedItems, comboChoiceID).reduce(
        (acc, e) => acc + e.quantity,
        0,
    )
}

function isEqualUsedItem(a: UsedItem, b: UsedItem) {
    return (
        a.comboChoiceId === b.comboChoiceId &&
        a.nodeItemId === b.nodeItemId &&
        isEqual(a.usedVariants, b.usedVariants) &&
        isEqual(a.usedModifiers, b.usedModifiers)
    )
}

/** finding similar item to merge with targetComboItem. Return new UsedItems object */
export function tryGroupSimilarUsedItemWithTargetUsedItem(
    usedItems: UsedItems,
    targetComboItemPath: string,
) {
    const targetUsedItem = usedItems[targetComboItemPath]
    if (!targetUsedItem) return usedItems
    const matchUsedItemPath = Object.keys(usedItems)
        .filter((comboItemPath) => comboItemPath !== targetComboItemPath)
        .find((comboItemPath) =>
            isEqualUsedItem(targetUsedItem, usedItems[comboItemPath]),
        )
    if (matchUsedItemPath) {
        const newUsedItems = { ...usedItems }
        targetUsedItem.quantity += newUsedItems[matchUsedItemPath].quantity
        delete newUsedItems[matchUsedItemPath]

        return newUsedItems
    } else return usedItems
}

export function isSameOrderItem(
    lineItemA: OrderItemViewModel,
    lineItemB: OrderItemViewModel,
) {
    // In case of duplicated items but be sent separately (KDS status can be different)
    // -> they should be different order lines in cart
    if (
        (lineItemA.kdsStatus !== OrderLineKdsStatus.Unknown ||
            lineItemB.kdsStatus !== OrderLineKdsStatus.Unknown) &&
        lineItemA.id !== lineItemB.id
    )
        return false

    const chainBool =
        (lineItemA.item.itemId === lineItemB.item.itemId ||
            lineItemA.item.itemId === lineItemB.item.baseItemId) &&
        isEqual(lineItemA.usedItems, lineItemB.usedItems) &&
        isEqual(lineItemA.usedModifiers, lineItemB.usedModifiers) &&
        isEqual(lineItemA.usedVariants, lineItemB.usedVariants) &&
        isEqual(
            lineItemA.kdsStatus ?? OrderLineKdsStatus.Unknown,
            lineItemB.kdsStatus ?? OrderLineKdsStatus.Unknown,
        ) &&
        lineItemA.note === lineItemB.note &&
        lineItemA.voidComp?.name === lineItemB.voidComp?.name &&
        // for Open Item, take an overridden name into account
        (lineItemA.displayName ?? '') === (lineItemB.displayName ?? '') &&
        compareAssignToLists(lineItemA, lineItemB)

    if (isPromotionItem(lineItemA) || isPromotionItem(lineItemB)) {
        return isAllSameDiscount(lineItemA, lineItemB) && chainBool
    }
    return chainBool
}

export const isUseSamePriceList = (
    menus: MenuViewModel[],
    priceListData: PriceListData,
    itemA: OrderItemViewModel,
    itemB: OrderItemViewModel,
) => {
    const itemApriceListId =
        menus.find((m) => itemA.menuId && m.menuId === itemA.menuId)
            ?.priceListID ?? priceListData.basePriceList.priceListID
    const itemBpriceListId =
        menus.find((m) => itemB.menuId && m.menuId === itemB.menuId)
            ?.priceListID ?? priceListData.basePriceList.priceListID

    return itemApriceListId === itemBpriceListId
}

/*
    Compares the 'assignTo' lists of two items.
    Always returns true for QS.
*/
const compareAssignToLists = (
    lineItemA: OrderItemViewModel,
    lineItemB: OrderItemViewModel,
) => {
    // In the case of QS, lineItemA.assignTo will have a value if the order is saved, but lineItemB.assignTo is always undefined - so return true.
    if (lineItemB.assignTo === undefined) {
        return true
    }

    return isSameAssignTo(lineItemA, lineItemB)
}

function isAllSameDiscount(
    orderA: OrderItemViewModel,
    orderB: OrderItemViewModel,
) {
    const isSameDiscount = (
        a: DiscountContractWithCustom,
        b: DiscountContractWithCustom,
    ) => {
        return (
            a.discountID === b.discountID &&
            a.value === b.value &&
            a.type === b.type &&
            a.isCustomDiscount === b.isCustomDiscount &&
            a.discountRedemptionId === b.discountRedemptionId
        )
    }
    return (
        (orderA.discounts?.length ?? 0) === (orderB.discounts?.length ?? 0) &&
        orderA.discounts?.every((a) =>
            orderB.discounts?.some((b) => isSameDiscount(a, b)),
        )
    )
}

function isSameAssignTo(
    orderA: OrderItemViewModel,
    orderB: OrderItemViewModel,
) {
    return isEqual(orderA.assignTo ?? [], orderB.assignTo ?? [])
}

export function isSameUsedModifier(a: UsedModifier, b: UsedModifier) {
    if (a.modifierOptionId !== b.modifierOptionId) return false

    const aBindingIds = Object.keys(a.subModifierOptions).sort()
    const bBindingIds = Object.keys(a.subModifierOptions).sort()

    if (!isEqual(aBindingIds, bBindingIds)) return false

    return aBindingIds.every((bindingId) => {
        const aSubModifierOptionIdsInBinding = a.subModifierOptions[
            bindingId
        ].sort()
        const bSubModifierOptionIdsInBinding = b.subModifierOptions[
            bindingId
        ].sort()

        return isEqual(
            aSubModifierOptionIdsInBinding,
            bSubModifierOptionIdsInBinding,
        )
    })
}
interface flatModifierOption {
    modifierOptionId: string
    bindingId: string
}
export function subModifierPrices(
    usedModifiers: UsedModifiers,
    modifierList: EntityModifierViewModel[],
    menuId: string | undefined,
    priceListData: PriceListData,
) {
    const allSelectedModifierOptionIds = keys(usedModifiers)
        .flatMap((bindingItem) => {
            return usedModifiers[bindingItem].flatMap((parentOp) => {
                const mods: flatModifierOption[] = [
                    {
                        modifierOptionId: parentOp.modifierOptionId,
                        bindingId: bindingItem,
                    },
                ]
                const subMods: flatModifierOption[] = keys(
                    parentOp.subModifierOptions,
                ).flatMap((bindingMod) => {
                    return parentOp.subModifierOptions[bindingMod].map<
                        flatModifierOption
                    >((opId) => {
                        return {
                            modifierOptionId: opId.modifierOptionId,
                            bindingId: bindingMod,
                        }
                    })
                })
                return mods.concat(subMods)
            })
        })
        .flat()
    let sum = new Decimal(0)
    allSelectedModifierOptionIds.forEach((mod) => {
        const binding = modifierList.find(
            (a) => a.entityModifierId === mod.bindingId,
        )
        const price = binding?.hasAssociatedCost
            ? getModifierOptionPrice(
                  mod.modifierOptionId,
                  menuId,
                  priceListData,
              )
            : 0
        sum = sum.plus(price)
    })
    return sum
}

export function lastIndexOf<T>(
    array: T[],
    predicateFn: (element: T) => boolean,
) {
    for (let i = array.length - 1; i >= 0; i--) {
        if (predicateFn(array[i])) {
            return i
        }
    }
    return -1
}

/**
 * check is valid Guid or not
 * There's use case because we introduce None value as '0' to support UX in modifier picker
 */
export function isValidId(id: string) {
    return id !== NONE_OPTION && id !== ''
}

export function isSentOrder(order: OrderContract | undefined) {
    return order?.status === OrderStatus.Sent
}
export function isNewOrder(order: OrderContract | undefined) {
    return (
        order?.status === OrderStatus.New ||
        order?.status === OrderStatus.Accepted ||
        order?.status === OrderStatus.Saved ||
        order?.status === OrderStatus.Sent
    )
}
export function isOrderHasPayment(order: OrderContract | undefined) {
    return isPartiallyPaidOrder(order) || isPaidOrder(order)
}
export function isPartiallyPaidOrder(order: OrderContract | undefined) {
    return order?.orderPaymentStatus === OrderPaymentStatus.PartiallyPaid
}
export function isPaidOrder(order: OrderContract | undefined) {
    return order?.orderPaymentStatus === OrderPaymentStatus.Paid
}
export const isComp = (voidComp?: VoidCompReasonModel) =>
    (voidComp && voidComp.type === VoidCompReasonType.Comp) || false

export const getModOptionName = (option: ModifierOptionContract) => {
    return option?.displayName ?? option?.itemName ?? '#unnamed'
}
export const displayOrderLineItemName = (
    orderLine: Pick<
        OrderLineLookupContract,
        'itemID' | 'item' | 'displayName' | 'sharedOrderLineID'
    >,
) => {
    const item = orderLine?.item
    const isSubSharedLine = Boolean(orderLine?.sharedOrderLineID)
    return displayItemName(
        {
            ...item,
            displayName: orderLine.itemID?.includes(OPEN_ITEM_ID)
                ? orderLine.displayName
                    ? `${orderLine.displayName} (Open Item)`
                    : 'Open Item'
                : orderLine.item?.displayName,
        },
        isSubSharedLine,
    )
}

export const displayCartItemName = (line: OrderItemViewModel) => {
    return displayOrderLineItemName({
        item: line.item,
        itemID: line.item.itemId,
        sharedOrderLineID: line.isSubSharedOrderLine ? 'shared' : undefined,
        displayName: `${line.displayName}`,
    })
}
type TItemName = Pick<ItemContract, 'name' | 'displayName'>
export const displayItemName = (
    item?: TItemName & {
        baseItem?: TItemName | null
    },
    isSharedLine: boolean = false,
) => {
    return (
        (item?.baseItem?.displayName ||
            item?.baseItem?.name ||
            item?.displayName ||
            item?.name ||
            '#unnamed') + (isSharedLine ? ' (Shared)' : '')
    )
}

/**
 * Return TRUE if order is fall in one of below conditions
 * 1) Order status is not either `New` or `Saved` or `Sent`
 * 2) Order is paid or partially paid
 */
export const isProcessedOrder = (
    editingOrder: OrderContract | OrderLookupContract | undefined,
) => {
    if (isNil(editingOrder)) return false
    return !isNewOrder(editingOrder) || isOrderHasPayment(editingOrder)
}
export const NONE_OPTION = '0'

/**
 * Find item (include variant item) in cached item map
 * @param itemMap prefer using sellState.itemMaps
 * @param searchItemId item ID to serach
 * @returns tuple of [searchItem, parentItem]
 */
type TFindItemResult = {
    searchItem: ItemViewModel
    parentItem: ItemViewModel | null
}
export function findItemViewModel(
    itemMap: ItemMap,
    searchItemId: string,
): TFindItemResult | undefined {
    const items = values(itemMap)

    for (const item of items) {
        if (item.itemId === searchItemId)
            return { searchItem: item, parentItem: null }

        if (item.variations && item.variations.length > 0) {
            const matchedVariantItem = item.variations.find(
                (i) => i.itemId === searchItemId,
            )
            if (matchedVariantItem)
                return { searchItem: matchedVariantItem, parentItem: item }
        }
    }

    return undefined
}

/**
 * Check Item availabilities of Single Menu Section
 * @param baseItemId
 * @param variantionIds
 * @param categoryID
 * @param selectedMenuSectionId
 * @param menu
 * @returns
 */
function checkItemAvaiabilityBySingleMenuSection(
    baseItemId: string,
    variantionIds: string[],
    categoryID: string,
    selectedMenuSectionId: string,
    menu?: MenuViewModel,
) {
    const menuItemTimeAvailabilities =
        findMenuItemDetail(menu, baseItemId, selectedMenuSectionId)
            ?.availabilities ?? []
    const hasSomeVariantAvailable =
        variantionIds.length === 0 ||
        variantionIds.some((variantionId) => {
            return validateTimeAvailability(
                findMenuItemDetail(menu, variantionId, selectedMenuSectionId)
                    ?.availabilities ?? [],
            )
        })

    return (
        hasSomeVariantAvailable &&
        validateTimeAvailability(menu?.availabilities ?? []) &&
        validateTimeAvailability(menuItemTimeAvailabilities ?? []) &&
        validateTimeAvailability(
            menu?.menuSectionAvailabilitiesMap?.[selectedMenuSectionId],
        ) &&
        validateTimeAvailability(
            menu?.menuItemCategoryAvailabilitiesMap?.[categoryID],
        )
    )
}

/**
 * Check items availability in time in selected menu
 * @param baseItemId prefer using sellState.items || useTableSaleSelectReducer.items
 * @param variantionIds item id of variants of item
 * @param selectedMenuSectionId
 * @param menu current selected menu
 * @returns list of availability items in selected menu
 */
export function checkItemTimeAvailabilityInMenu(
    baseItemId: string,
    variantionIds: string[],
    categoryID: string,
    selectedMenuSectionId: string,
    menu?: MenuViewModel,
) {
    if (selectedMenuSectionId === MENUSECTION_ALL_ITEMS) {
        const nonAllItemsSections =
            menu?.menuSections.filter(
                (s) => s.menuSectionId !== MENUSECTION_ALL_ITEMS,
            ) ?? []

        if (isEmpty(nonAllItemsSections))
            return checkItemAvaiabilityBySingleMenuSection(
                baseItemId,
                variantionIds,
                categoryID,
                selectedMenuSectionId,
                menu,
            )

        //1st: Find available Menu section
        const availableMenuSectionIDs =
            nonAllItemsSections
                .filter((s) =>
                    validateTimeAvailability(
                        menu?.menuSectionAvailabilitiesMap?.[s.menuSectionId] ??
                            [],
                    ),
                )
                .flatMap((ms) => ms.menuSectionId) ?? []

        //2nd: Find available Menu Items base on available menu sections
        var isAvailable = false
        availableMenuSectionIDs.forEach((id) => {
            if (
                checkItemTimeAvailabilityInMenu(
                    baseItemId,
                    variantionIds,
                    categoryID,
                    id,
                    menu,
                )
            ) {
                isAvailable = true
                return
            }
        })

        return isAvailable
    }

    return checkItemAvaiabilityBySingleMenuSection(
        baseItemId,
        variantionIds,
        categoryID,
        selectedMenuSectionId,
        menu,
    )
}
export function findMenuItemDetail(
    menu: MenuViewModel | undefined,
    itemId: string,
    selectedMenuSectionId: string,
): IMenuItemDetail | undefined {
    return values(menu?.menuItemDetailMap ?? {}).find(
        (a) =>
            a.itemID === itemId &&
            (selectedMenuSectionId === MENUSECTION_ALL_ITEMS ||
                a.menuSectionID === selectedMenuSectionId),
    )
}
export function checkModifierOptionAvailable(
    menuSectionId: string | undefined,
    menuItem: IMenuItemDetail | undefined,
    modifierOptionId: string,
): boolean {
    if (!menuSectionId || menuSectionId === MENUSECTION_ALL_ITEMS || !menuItem)
        return true
    if ((menuItem?.menuItemModifierOptions?.length ?? 0) === 0) return true
    return (
        menuItem?.menuItemModifierOptions?.find(
            (a) => a.modifierOptionID === modifierOptionId,
        )?.isAvailable ?? true
    )
}
export function checkMenuItemAvailable(
    menu: MenuViewModel | undefined,
    item: ItemViewModel,
    selectedMenuSectionId: string,
): boolean {
    return (
        findMenuItemDetail(menu, item.itemId, selectedMenuSectionId)
            ?.isAvailable ||
        //baseItem should not has menuItemDetail, so the result will be true
        item.variations.length > 0 ||
        item.depositAndCreditItems.length > 0
    )
}
/**
 * Get Item name
 * @param itemMap prefer using sellState.itemMaps
 * @param searchItemId item ID to search
 * @returns name of item. If item type is 'variant item', this function will return variant name e.g., 'Americano M Iced'
 */
export function getItemName(
    itemMap: ItemMap,
    searchItemId: string,
): string | undefined {
    const result = findItemViewModel(itemMap, searchItemId)
    if (!result) return undefined

    return result.searchItem.displayName ?? result.searchItem.name
}

/**
 * Filter item by its name
 * @param searchString
 * @param itemsBeforeFilter
 * @returns
 */
export function filterByItemNameOrBarcode(
    searchString: string,
    itemsBeforeFilter: ItemViewModel[],
) {
    const reg = RegExp(searchString.replace(/[^ก-๛\w\s]/g, ''), 'i')

    // Filter by name or barcode.
    return itemsBeforeFilter.filter(
        (item) =>
            (item?.displayName?.search(reg) ?? -1) !== -1 ||
            (item?.barcode?.search(reg) ?? -1) !== -1,
    )
}
export function toTableOrderLineContract(
    orderItem: OrderItemViewModel,
    itemsMap: ItemMap,
    isSend?: boolean,
): TableServiceOrderLineContract {
    const lineContract = toOrderLineContract(
        orderItem,
        orderItem.status ?? OrderLineStatus.New,
        itemsMap,
    )
    if (!orderItem.assignTo || isEmpty(orderItem.assignTo))
        throw new Error('`assignTo` is required in table service')

    return {
        orderLine: lineContract,
        isSend,
        targetOrderIDs: orderItem.assignTo ?? [],
    }
}

export function toQuickServiceOrderLineContract(
    orderItem: OrderItemViewModel,
    nextOrderLineStatus: OrderLineStatus,
    itemsMap: ItemMap,
    isSend?: boolean,
    taxLinesMap?: Dictionary<LineResultModel>,
    defaultSortingIndex?: number,
): OrderLineContract {
    const isFree = isFreeItem(orderItem)
    const lineContract = toOrderLineContract(
        orderItem,
        nextOrderLineStatus,
        itemsMap,
    )
    const lineTax = taxLinesMap?.[orderItem.id]
    //for send flag for quick service only
    lineContract.isSendToKDS = isSend
    //for order line validation (QS only)
    lineContract.subtotal = isFree
        ? 0
        : round(
              new Decimal(orderItem.lineTotal).mul(orderItem.amount),
          ).toNumber()
    lineContract.totalTaxAmount = isFree ? 0 : lineTax?.taxAmount ?? 0
    lineContract.sortingIndex = lineTax?.sortingIndex ?? defaultSortingIndex
    lineContract.tempOrderLineID = orderItem.isSaved ? null : orderItem.id
    return lineContract
}
function toOrderLineContract(
    orderItem: OrderItemViewModel,
    nextOrderLineStatus: OrderLineStatus,
    itemsMap: ItemMap,
) {
    let item = orderItem.item
    // in case variant item
    if (
        Object.keys(orderItem.usedItems).length === 0 &&
        orderItem.usedVariants
    ) {
        const vItem = getVariationItem(
            orderItem.item,
            values(orderItem.usedVariants),
        )
        if (vItem) item = vItem
    }

    const itemId = item.itemId
    const comboLineItems: OrderLineContract[] = getComboLine(
        orderItem,
        nextOrderLineStatus,
        itemsMap,
    )

    const modifierLines = getOrderLineModifiers(orderItem.usedModifiers)
    const orderLineContract: OrderLineContract = {
        orderLineID:
            orderItem.isSaved && Guid.isValid(orderItem.id)
                ? orderItem.id
                : undefined,

        itemID: itemId,
        menuID: orderItem?.menuId,
        menuSectionID:
            orderItem?.menuSectionId === MENUSECTION_ALL_ITEMS
                ? undefined
                : orderItem?.menuSectionId,
        orderQuantity: orderItem.amount,
        orderLineDiscountAmount: isFreeItem(orderItem)
            ? 0
            : new Decimal(orderItem.itemDiscountAmount ?? 0)
                  .plus(orderItem.totalCompDiscount ?? 0)
                  .toNumber(),

        note: orderItem.note,
        orderLineModifiers: modifierLines,
        comboLineItems,
        status: nextOrderLineStatus,
        voidComp: orderItem.voidComp,
        // * : Attach discounts for Line-level
        discounts: (orderItem.discounts ?? []).map(
            (i): OrderDiscountModel => ({
                orderDiscountID: i.orderLineDiscountId,
                discountType: i.isCustomDiscount
                    ? OrderDiscountType.CustomDiscount
                    : OrderDiscountType.PredefinedDiscount,
                discountID: i.isCustomDiscount ? undefined : i.discountID,
                discountValue: i.value,
                isPercentage: i.type === DiscountType.Percentage,
                discountRedemptionID: i.discountRedemptionId,
            }),
        ),
        sharedOrderLineID: orderItem.sharedOrderLineID,
        isAutoAppliedDiscount: orderItem.isAutoAppliedDiscount ?? null,
        seatNote: orderItem.seatNote,
    }

    // exclusive for "Open Item" line
    if (isOpenItemId(orderItem.item.itemId)) {
        orderLineContract.displayName = orderItem.displayName
        orderLineContract.price = orderItem.lineTotal
        orderLineContract.taxID = orderItem.overriddentaxID
        orderLineContract.isTaxInclusive = orderItem.overriddenIsTaxInclusive
    }

    return orderLineContract
}

function getComboLine(
    orderItem: OrderItemViewModel,
    nextOrderLineStatus: OrderLineStatus,
    itemsMap: ItemMap,
) {
    return keys(orderItem.usedItems).map((path) => {
        const cLine: OrderLineContract = {}

        const modLines = getOrderLineModifiers(
            orderItem.usedItems[path].usedModifiers,
        )

        const choiceId = path.split(',')[0]

        const itemVariantOptions = values(
            orderItem.usedItems[path].usedVariants,
        )

        const baseItemId = orderItem.usedItems[path].nodeItemId
        if (itemVariantOptions.length > 0) {
            // resolve to variant item ID
            // note that usedItems[path].nodeItemId is base item id
            const variantItem = getVariationItem(
                itemsMap[baseItemId]!!,
                itemVariantOptions,
            )
            cLine.itemID = variantItem?.itemId
        } else cLine.itemID = baseItemId
        cLine.comboChoiceID = choiceId
        cLine.orderLineVariantOptionIds = itemVariantOptions
        cLine.orderLineModifiers = modLines
        cLine.orderQuantity = orderItem.usedItems[path].quantity
        cLine.status = nextOrderLineStatus

        return cLine
    })
}

export function getOrderLineModifiers(usedModifiers: UsedModifiers) {
    return keys(usedModifiers).flatMap((parentBinding) => {
        const modWithQuantity = usedModifiers[parentBinding].reduce<
            usedModWithQuantity[]
        >((newMod, cur) => {
            const existMod = newMod.find(
                (mod) =>
                    mod.modifierOptionId === cur.modifierOptionId &&
                    isEqual(mod.subModifierOptions, cur.subModifierOptions),
            )
            if (existMod) existMod.quantity++
            else
                newMod.push({
                    ...cur,
                    quantity: 1,
                })
            return newMod
        }, [])
        return modWithQuantity.flatMap((parentMod) => {
            const modLine: OrderLineModifierProcessContract = {
                modifierOptionID: parentMod.modifierOptionId,
                quantity: parentMod.quantity,
                priceAdd: parentMod.addPrice ?? 0,
                bindingModifierGroupID: parentBinding,
                subModifiers: [],
            }
            modLine.subModifiers = keys(parentMod.subModifierOptions).flatMap(
                (binding) => {
                    return parentMod.subModifierOptions[binding]
                        .filter((op) => op.modifierOptionId !== NONE_OPTION)
                        .reduce<usedModWithQuantity[]>((newMod, cur) => {
                            const existMod = newMod.find(
                                (mod) =>
                                    mod.modifierOptionId ===
                                    cur.modifierOptionId,
                            )
                            if (existMod) existMod.quantity++
                            else
                                newMod.push({
                                    modifierOptionId: cur.modifierOptionId,
                                    addPrice: cur.addPrice,
                                    quantity: 1,
                                    subModifierOptions: {},
                                })
                            return newMod
                        }, [])
                        .map<OrderLineModifierProcessContract>((s) => {
                            return {
                                modifierOptionID: s.modifierOptionId,
                                priceAdd: s.addPrice,
                                quantity: s.quantity,
                                bindingModifierGroupID: binding,
                                subModifiers: [],
                            }
                        })
                },
            )
            return modLine
        })
    })
}

/**
 * validate that is invalid transaction and not void yet
 * @param currentPaymentActivity
 * @param AllPaymentIntegrationActivities
 */
export const isInvalidTransaction = (
    currentPaymentActivity: PaymentIntegrationActivityContract,
    AllPaymentIntegrationActivities: PaymentIntegrationActivityContract[],
) => {
    return (
        !currentPaymentActivity.paymentID &&
        PAYMENT_INTEGRATION_COMPLETE.includes(
            currentPaymentActivity.transactionStatus!,
        ) &&
        currentPaymentActivity.eventType === PaymentIntegrationEventType.Sale &&
        //not is already void transaction
        !AllPaymentIntegrationActivities?.some(
            (voidedTranasaction) =>
                voidedTranasaction.referencePaymentIntegrationActivityID ===
                    currentPaymentActivity.paymentIntegrationActivityID &&
                PAYMENT_INTEGRATION_RETURN_TYPES.includes(
                    voidedTranasaction.eventType!,
                ) &&
                PAYMENT_INTEGRATION_COMPLETE.includes(
                    voidedTranasaction.transactionStatus!,
                ),
        )
    )
}

export type PriceTuple = {
    item: ItemViewModel
    priceList: POSPriceListContract
    price: number
    appliedByMenus: MenuViewModel[]
}

export const getProductPriceListPairFromAllMenus = (
    menus: MenuViewModel[],
    priceListData: PriceListData,
    shouldFilterTimeAvailability: boolean,
): PriceTuple[] => {
    const priceTuples: PriceTuple[] = []
    const menuMap: Map<string, MenuViewModel[]> = new Map<
        string,
        MenuViewModel[]
    >()

    menus.forEach((menu) => {
        menu.items.forEach((menuItem) => {
            const [usedPriceList, price] = getItemPriceWithAppliedPriceList(
                menuItem.itemId,
                menu.menuId,
                priceListData,
            )

            if (shouldFilterTimeAvailability) {
                if (
                    !checkItemTimeAvailabilityInMenu(
                        menuItem.itemId,
                        menuItem.variations.map((v) => v.itemId),
                        menuItem.categoryId,
                        MENUSECTION_ALL_ITEMS,
                        menu,
                    )
                )
                    return
            }

            const key = `${menuItem.itemId}_${usedPriceList.priceListID}`
            if (!menuMap.has(key)) {
                menuMap.set(key, [menu])
                priceTuples.push({
                    item: menuItem,
                    priceList: usedPriceList,
                    price,
                    appliedByMenus: [],
                })
            } else {
                menuMap.get(key)!.push(menu)
            }
        })
    })

    priceTuples.forEach((pt) => {
        pt.appliedByMenus = menuMap.get(
            `${pt.item.itemId}_${pt.priceList.priceListID}`,
        )!
    })

    return priceTuples
}

export const isOrderNotComplete = (order: OrderLookupContract) => {
    return (
        !(
            order!.orderPaymentStatus === OrderPaymentStatus.Paid ||
            order!.orderPaymentStatus === OrderPaymentStatus.Refunded ||
            order!.orderPaymentStatus === OrderPaymentStatus.Voided
        ) ||
        order?.orderLines?.some(
            (i) =>
                (!i.isSharedLine ||
                    (i.isSharedLine &&
                        i.subSharedOrderLines?.some(
                            (i) => i.status !== OrderLineStatus.Completed,
                        ))) &&
                i.status !== OrderLineStatus.Voided &&
                i.kdsStatus === OrderLineKdsStatus.InPreparation,
        )
    )
}
